import * as React from "react";
import { z } from "zod";
import { DynamicForm } from "../../util/DynamicForm";
import { useGeneratedForm } from "../../hooks/useGeneratedForm";
import { apiGet, apiPost } from "../../util/axiosUtil";
import API_URL from "../../config/apiConfig";
import MutationHandler from "../../util/MutationHandler";

const FETCH_MEETINGS_URL = `${API_URL}/api/meetings/without-host`;
const CREATE_HOST_URL = `${API_URL}/api/hosts`;

export default function CreateHosts({ onCancel, onSuccess }) {
  const [meetingOptions, setMeetingOptions] = React.useState([]);
  const [loadingmeetings, setLoadingMeetings] = React.useState(true);

  // Fetch available plans
  React.useEffect(() => {
    const fetchMeetings = async () => {
      // await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const response = await apiGet(FETCH_MEETINGS_URL);
        const options =
          response?.data?.data?.map((meeting) => ({
            id: meeting.id,
            name: meeting.meetingNumber,
          })) || [];
        setMeetingOptions(options);
        setLoadingMeetings(false);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchMeetings();
  }, []);

  const formConfig = {
    name: {
      label: "Name",
      type: "text",
      placeholder: "Enter your name",
      defaultValue: "",
      required: true,
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name is required",
          });
        } else if (value.length < 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name should be more than 3 letters",
          });
        }
      }),
    },
    username: {
      label: "Username",
      type: "text",
      placeholder: "Enter username",
      defaultValue: "",
      required: true,
      normalize: (val) => val.toLowerCase().replace(/\s+/g, ""),
      validation: z
        .string()
        .transform((val) => val.toLowerCase().replace(/\s+/g, "")) // ✅ auto-make lowercase and remove spaces
        .superRefine((value, ctx) => {
          if (!value.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Username is required",
            });
          } else if (value.length < 3) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Username should be more than 3 letters",
            });
          } else if (!/^[a-z0-9_]+$/.test(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Username can only contain lowercase letters, numbers, and underscores",
            });
          }
        }),
    },
    password: {
      label: "Password",
      type: "password",
      placeholder: "Enter password",
      defaultValue: "",
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z
        .string()
        .nonempty("Password is required") // ✅ ensures not empty
        .min(4, "Password must be at least 4 characters"),
    },
    meeting: {
      label: "Meeting",
      type: "select",
      options: meetingOptions,
      defaultValue: "",
      required: false,
      isLoadingOptions: loadingmeetings,
      validation: z.preprocess(
        (val) => (val === "" || val == null ? undefined : Number(val)),
        z.number().positive("Invalid meeting").optional()
      ),
    },
  };

  return (
    <MutationHandler
      mutationFn={(formData) => apiPost(CREATE_HOST_URL, formData)}
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <ManagerForm
          formConfig={formConfig}
          onSubmit={(value) => mutation.mutate(value)}
          isLoading={mutation.isPending}
          onCancel={onCancel}
        />
      )}
    </MutationHandler>
  );
}

function ManagerForm({ formConfig, onSubmit, isLoading, onCancel }) {
  const form = useGeneratedForm(formConfig, async ({ value }) => {
    onSubmit(value);
  });

  return (
    <DynamicForm
      form={form}
      formConfig={formConfig}
      isLoading={isLoading}
      onCancel={onCancel}
      buttonText={"Create Host"}
    />
  );
}
